import { Modal, Tab } from 'bootstrap';
import { createOrUpdate } from '../../services/returnRequestService';
import { addItem } from '../../services/CartService';
import { NotificationTypes } from "../../typescript/enums/enums";
import NotificationClass from '../../typescript/classes/NotificationClass';
import { store } from '../../redux/store';
import { setCartSize } from '../../redux/reducers/cartSlice.reducer';
import { sendErrorDescription } from '../../services/bookDetailsService';
import { useErrorHandler } from '../../hooks/useErrorHandler';

type NavigationDirection = 'next' | 'prev' | null;

export default class productPageClass {

  handleError = useErrorHandler();

  private isNavigating: boolean = false;
  private abortControler: AbortController | null = null;

  static init() {
    const productPage = document.getElementById('product-page');

    if (productPage) {
      // create an instance of product page class to run constructor functions
      new productPageClass();
    }
  }

  constructor() {
    this.initTabs();
    this.enableDistributorSwitch();
    //this.enableReturnRequests();
    this.enableAddItemToCart();
    this.enableReportError();
    this.initNavigation();
    this.initNavigationPop();
  }

  initNavigationPop() {
    window.addEventListener('popstate', async () => {
      await this.navigateTo(window.location.href, true, 'prev');
    });
  }

  initNavigation() {
    const navigators = document.querySelectorAll<HTMLAnchorElement>(`[data-navigate-to]`);

    navigators.forEach(navigator => {
      navigator.addEventListener('click', async (clickEvent) => {
        clickEvent.preventDefault();
        const url = navigator.getAttribute('data-navigate-to');
        const direction = navigator.getAttribute('data-navigate-direction') as NavigationDirection;
        if (url) {
          await this.navigateTo(url, false, direction);
        }
      })
    })
  }

  async navigateTo(url: string, isPop: boolean, direction: NavigationDirection) {

    if (this.isNavigating) {
      this.abortControler?.abort();
    }

    this.isNavigating = true;
    this.abortControler = new AbortController();

    try {
      const page = document.getElementById('product-page');
      if (page) {
        const response = await fetch(
          `${url}?direction=${direction}`, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            signal: this.abortControler.signal
        });

        // set up transition fading
        page.classList.add('fade');
        await new Promise(r => setTimeout(r, 200));

        // replace html
        const html = await response.text();

        // we need to counter fast clicking before trying to set new html.
        // A previous call may have already set this which will result in us changing
        // outerHtml on a node no longer in the DOM
        if (!page?.parentNode) return;

        page.outerHTML = html;

        // add 'fade' to set opacity = 0
        const top = document.getElementById('product-page');
        top?.classList.add('fade');

        // Wait for 2 animframes, first will let the fading class set opacity = 0
        // next will remove the 'fade'-class and opacity will return to 1.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => top?.classList.remove('fade'));
        });

        // Skip history if this is a popstate
        !isPop && window.history.pushState({}, '', url);

        // re-init stuff that affects elements replaced in fetch
        this.initTabs();
        this.initNavigation();
        this.enableDistributorSwitch();
        this.enableAddItemToCart();
        this.enableReportError();
      }
    }
    catch (e: unknown) {
      if (e instanceof Error && e.name !== 'AbortError') throw e;
    }
    finally {
      this.abortControler = null;
      this.isNavigating = false;
    }
}

  initTabs() {
    var triggerTabList = [].slice.call(
      document.querySelectorAll('#product-tabs a')
    );
    triggerTabList.forEach(function (triggerEl: Element) {
      var tabTrigger = new Tab(triggerEl);

      triggerEl.addEventListener('click', function (event) {
        event.preventDefault();
        tabTrigger.show();
      });
    });
  }

  parseQuantity(value: string | undefined) {
    if (value == null || value === "") {
      const message = "Antal får inte vara tomt.";
      throw new Error(message);
    }

    const quantity: number = Number.parseInt(value)

    if (isNaN(quantity)) {
      const message = `'${value}' är ett felaktigt antal.`;
      throw new Error(message);
    }

    return quantity;
  }

  enableReportError() {

    const reportErrorButton = document.getElementById('report-error-button');
    const catalogEntryCode = reportErrorButton?.getAttribute('data-code') as string;

    reportErrorButton?.addEventListener('click', function (event) {
      const errorDescription = (document.getElementById('errormessage') as HTMLTextAreaElement)?.value || ''
      if (errorDescription.length < 4) {
        document.getElementById('errormessage')?.classList.add("u-border-error");
        document.getElementById('errormessage-empty')?.classList.remove("d-none");
        return;
      }

      sendErrorDescription(catalogEntryCode, errorDescription).then(res => {
        res && NotificationClass.createNotification({
          type: NotificationTypes.Success,
          message: 'Meddelandet har skickats!'
        });

        !res && NotificationClass.createNotification({
          type: NotificationTypes.Error,
          message: 'Meddelandet kunde inte skickas!'
        });

        (document.getElementById('errormessage') as HTMLTextAreaElement).value = "";
        let modal = Modal.getInstance(document.getElementById('reportErrorModal') as any)
        modal?.hide();
      });
    })

  }

  enableAddItemToCart() {
    const addItemElement = document.getElementById('add-to-cart');

    addItemElement?.addEventListener('click', () => {
      const distributors = document.querySelectorAll(`[data-distributor-trigger]`);
      let selectedDistributor = -1;
      let selectedDistributorShortName = "";

      distributors.forEach((el: Element) => {
        if ((el as HTMLInputElement).checked) {
          let parseValue = el.getAttribute('data-distributor-trigger');
          if (parseValue) {
            selectedDistributor = Number.parseInt(parseValue);
          }
          selectedDistributorShortName = el.id;
        }
      });

      const catalogEntryCode = addItemElement.getAttribute('data-code');

      const quantityInputElement = document.getElementById('quantity-input') as HTMLInputElement | null;

      let quantity: number | undefined;
      let quantityString: string = quantityInputElement?.value || "1"; // fall back to 1, since placeholder on input field is "1"

      try {
        // Parse the input value to a number; throws an error if the value is invalid
        quantity = this.parseQuantity(quantityString) ?? 1;
      } catch (error:any) {
        this.handleError(error);

        return;
      }

      const reference = (document.getElementById('addToCartReference') as HTMLInputElement)?.value || '';

      if (selectedDistributor > 0 && catalogEntryCode) {
        addItem({ catalogEntryCode, quantity, selectedDistributorShortName, reference, includeInOrder: true })
          .then((res) => {
            NotificationClass.createNotification({
              type: NotificationTypes.Success,
              message: 'Artikeln lades till i korgen'
            });
            store.dispatch(setCartSize(res?.data
              .map((p: any) => p.quantity)
              .reduce((p: any, c: any) => p + c, 0)));

            // clear input element
            const referenceInput = document.getElementById("addToCartReference") as HTMLInputElement;
            if (referenceInput) {
              referenceInput.value = "";
            }

          }).catch((error) => this.handleError(error)) // Handle error using the centralized handleError function
      }

      this.setFocusOnMainSearch();
    });
  }

  enableDistributorSwitch() {
    const elements = [].slice.call(
      document.querySelectorAll('[data-distributor-trigger]')
    );

    elements.forEach((el: Element) => {
      el.addEventListener('change', function (event) {
        event.preventDefault();
        const trigger = el.getAttribute('data-distributor-trigger');
        const targets = document.querySelectorAll(`[data-distributor-target]`);

        targets.forEach((el: Element) => {
          el.getAttribute('data-distributor-target') === trigger
            ? el.classList.remove('d-none')
            : el.classList.add('d-none');
        });
      });
    });
  }

  enableReturnRequests() {
    const returnRequestModal = new Modal('#returnRequestModal');

    const form = document.querySelector('#returnRequestForm');

    const splitPath = window.location.pathname.split('/').filter((p) => p);
    const articleNumber = splitPath[splitPath.length - 1];

    form?.addEventListener('submit', (e) => {
      e.preventDefault();

      const { quantity, distributors } = (e.target as any).elements;
      const distributorArray: any[] = Array.from(distributors);

      createOrUpdate(
        articleNumber,
        distributorArray.filter((d: any) => d.checked)[0].value,
        quantity.value
      )
        .then((res) => {
          console.log(res);
          returnRequestModal.hide();
        })
        .catch((err) => {
          console.log(err);
        });
    });
  }

  setFocusOnMainSearch() {
    const mainSearchTextbox: HTMLInputElement = document.getElementById("main-search-textbox") as HTMLInputElement;

    if (mainSearchTextbox) {
      mainSearchTextbox.focus();
      mainSearchTextbox.select();
    };

  }
}
